import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Check, Sparkles, Plus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { trackFormSubmit, trackEvent } from "@/lib/analytics";
import { useSEO } from "@/hooks/useSEO";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { freeTrialFormSchema, freeTrialStepSchemas, type FreeTrialFormData } from "@/lib/validations/freeTrialForm";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

const industries = [
  "Automotive",
  "Fashion, Footwear & Apparel",
  "Outdoors",
  "Pet Products",
  "Sports & Recreation",
  "Other"
];

const countries = [
  "United States", "Canada", "United Kingdom", "Australia", "Germany", "France", 
  "Spain", "Italy", "Netherlands", "Belgium", "Switzerland", "Austria", "Sweden",
  "Norway", "Denmark", "Finland", "Ireland", "New Zealand", "Singapore", "Japan",
  "South Korea", "India", "Brazil", "Mexico", "Argentina", "Chile", "Colombia",
  "United Arab Emirates", "Saudi Arabia", "South Africa", "Israel", "Poland",
  "Czech Republic", "Portugal", "Greece", "Turkey", "Thailand", "Malaysia",
  "Indonesia", "Philippines", "Vietnam", "China", "Hong Kong", "Taiwan", "Other"
];

const ecommercePlatforms = [
  "Shopify", "WooCommerce", "Magento", "BigCommerce", "Salesforce Commerce Cloud",
  "Adobe Commerce", "SAP Commerce Cloud", "Oracle Commerce", "Custom Built",
  "Wix eCommerce", "Squarespace Commerce", "PrestaShop", "OpenCart", "Volusion",
  "3dcart", "Shift4Shop", "Ecwid", "Big Cartel", "Weebly", "Other"
];

const technologies = [
  "ERP System", "CRM (Salesforce, HubSpot)", "PIM System", "WMS System",
  "Payment Gateway", "Shipping Software", "Analytics Platform", "Marketing Automation",
  "Customer Service Platform", "Inventory Management", "Order Management",
  "Content Management System", "Email Marketing", "Social Media Management", "Other"
];

export default function FreeTrial() {
  useSEO({
    title: 'Start Your Free Trial | Ommni Commerce Platform',
    description: 'Get started with Ommni\'s commerce operations platform. Try our product information, pricing, and inventory management tools with a free trial.',
    keywords: 'free trial, commerce platform trial, PIM trial, inventory management free trial',
    canonical: 'https://ommni.io/free-trial',
  });

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [teamMembers, setTeamMembers] = useState<string[]>([""]);

  const form = useForm<FreeTrialFormData>({
    resolver: zodResolver(freeTrialFormSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      businessName: "",
      country: "",
      industry: "",
      currentPlatform: "",
      otherTech: [],
      teamMembers: [""],
    },
  });

  // Track initial form load
  useEffect(() => {
    trackEvent('free_trial_form_start', {
      category: 'form_engagement',
      label: 'form_loaded',
      step_number: 1,
      step_name: 'contact',
    });
  }, []);

  const addTeamMember = () => {
    const newMembers = [...teamMembers, ""];
    setTeamMembers(newMembers);
    form.setValue('teamMembers', newMembers);
  };

  const removeTeamMember = (index: number) => {
    const newMembers = teamMembers.filter((_, i) => i !== index);
    setTeamMembers(newMembers);
    form.setValue('teamMembers', newMembers);
  };

  const updateTeamMember = (index: number, value: string) => {
    const newMembers = [...teamMembers];
    newMembers[index] = value;
    setTeamMembers(newMembers);
    form.setValue('teamMembers', newMembers);
  };

  const toggleTechnology = (tech: string) => {
    const current = form.getValues('otherTech');
    const updated = current.includes(tech)
      ? current.filter(t => t !== tech)
      : [...current, tech];
    form.setValue('otherTech', updated);
  };

  const validateStep = async () => {
    const stepSchemas = [
      freeTrialStepSchemas.step1,
      freeTrialStepSchemas.step2,
      freeTrialStepSchemas.step3,
      freeTrialStepSchemas.step4,
    ];
    
    const currentSchema = stepSchemas[currentStep - 1];
    const values = form.getValues();
    
    try {
      await currentSchema.parseAsync(values);
      return true;
    } catch {
      return false;
    }
  };

  const handleNext = async () => {
    const isValid = await validateStep();
    
    if (isValid) {
      // Track step completion
      trackEvent('free_trial_step_complete', {
        category: 'form_engagement',
        label: `step_${currentStep}_complete`,
        step_number: currentStep,
        step_name: ['contact', 'industry', 'platform', 'team'][currentStep - 1],
      });
      
      setCurrentStep(prev => prev + 1);
      
      // Track step view for next step
      trackEvent('free_trial_step_view', {
        category: 'form_engagement',
        label: `step_${currentStep + 1}_view`,
        step_number: currentStep + 1,
        step_name: ['contact', 'industry', 'platform', 'team'][currentStep],
      });
    } else {
      // Trigger validation to show errors
      await form.trigger();
      
      // Track validation failure
      trackEvent('free_trial_step_validation_error', {
        category: 'form_engagement',
        label: `step_${currentStep}_validation_error`,
        step_number: currentStep,
      });
      
      toast({
        title: "Please complete all required fields",
        description: "Check the form for errors and try again.",
        variant: "destructive"
      });
    }
  };

  const handleBack = () => {
    // Track step back navigation
    trackEvent('free_trial_step_back', {
      category: 'form_engagement',
      label: `step_${currentStep}_back`,
      from_step: currentStep,
      to_step: currentStep - 1,
    });
    
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Track final step completion attempt
    trackEvent('free_trial_step_complete', {
      category: 'form_engagement',
      label: 'step_4_complete',
      step_number: 4,
      step_name: 'team',
    });
    
    try {
      const data = form.getValues();
      const submissionData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        businessName: data.businessName,
        country: data.country,
        industry: data.industry,
        currentPlatform: data.currentPlatform,
        otherTech: data.otherTech.join(", "),
        teamMembers: teamMembers.filter(email => email.trim() !== "").join(", ")
      };
      
      const { error } = await supabase.functions.invoke("send-trial-to-slack", {
        body: submissionData
      });

      if (error) throw error;

      // Track successful form submission
      trackFormSubmit('free_trial_form', true);
      
      // Track conversion event for GA4
      trackEvent('conversion', {
        category: 'conversion',
        label: 'free_trial_signup',
        value: 1,
        send_to: 'AW-CONVERSION',
        currency: 'USD',
        transaction_id: `trial_${Date.now()}`,
      });
      
      setIsSuccess(true);
    } catch (error: any) {
      trackFormSubmit('free_trial_form', false);
      
      // Track submission error
      trackEvent('free_trial_submission_error', {
        category: 'form_engagement',
        label: 'submission_failed',
        error_type: error?.message || 'unknown',
      });
      
      toast({
        title: "Submission failed",
        description: "Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 px-4">
        <div className="max-w-2xl w-full text-center space-y-8 animate-in fade-in duration-500">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
            <Check className="w-10 h-10 text-primary-foreground" />
          </div>
          <div className="space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold">We're Excited to Have You!</h1>
            <p className="text-xl text-muted-foreground max-w-xl mx-auto">
              Your access will be granted in the next 12-24 hours and one of our team members will be in touch.
            </p>
          </div>
          <Button size="lg" onClick={() => navigate("/")} className="mt-8">
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            Free Trial Setup
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">Let's Get You Started</h1>
          <p className="text-muted-foreground text-lg">Just a few questions to personalize your experience</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  currentStep >= step 
                    ? 'bg-primary text-primary-foreground scale-110' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step}
                </div>
                {step < 4 && (
                  <div className={`flex-1 h-1 mx-2 rounded-full transition-all ${
                    currentStep > step ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between max-w-2xl mx-auto mt-2 text-sm text-muted-foreground">
            <span>Contact</span>
            <span>Industry</span>
            <span>Platform</span>
            <span>Team</span>
          </div>
        </div>

        {/* Form Steps */}
        <Form {...form}>
          <div className="max-w-2xl mx-auto bg-card border rounded-2xl p-8 shadow-lg">
            {/* Step 1: Contact Details */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Tell us about yourself</h2>
                  <p className="text-muted-foreground">We'll use this to set up your account</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john@company.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="businessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Your Company Inc." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select your country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-popover z-50">
                          {countries.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Step 2: Industry */}
            {currentStep === 2 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div>
                  <h2 className="text-2xl font-bold mb-2">What's your industry?</h2>
                  <p className="text-muted-foreground">This helps us customize your experience</p>
                </div>
                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="space-y-4">
                          {industries.map((industry) => (
                            <button
                              key={industry}
                              type="button"
                              onClick={() => field.onChange(industry)}
                              className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                                field.value === industry
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{industry}</span>
                                {field.value === industry && (
                                  <Check className="w-5 h-5 text-primary" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Step 3: Platform & Tech Stack */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Your current setup</h2>
                  <p className="text-muted-foreground">Tell us about your tech stack</p>
                </div>
                <FormField
                  control={form.control}
                  name="currentPlatform"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current eCommerce Platform *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select your platform" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-popover z-50">
                          {ecommercePlatforms.map((platform) => (
                            <SelectItem key={platform} value={platform}>
                              {platform}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="space-y-3">
                  <Label>Other Technologies in Your Tech Stack</Label>
                  <p className="text-sm text-muted-foreground">Select all that apply</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto p-1">
                    {technologies.map((tech) => {
                      const isChecked = form.watch('otherTech').includes(tech);
                      return (
                        <div key={tech} className="flex items-center space-x-2">
                          <Checkbox
                            id={tech}
                            checked={isChecked}
                            onCheckedChange={() => toggleTechnology(tech)}
                          />
                          <Label
                            htmlFor={tech}
                            className="text-sm font-normal cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {tech}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Team Members */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Invite your team</h2>
                  <p className="text-muted-foreground">Add team members who should have access (optional)</p>
                </div>
                <div className="space-y-4">
                  <Label>Team Member Emails</Label>
                  {teamMembers.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => updateTeamMember(index, e.target.value)}
                        placeholder="teammate@company.com"
                        className="flex-1"
                      />
                      {teamMembers.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeTeamMember(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTeamMember}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Team Member
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    You can always add team members later
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1 || isSubmitting}
                type="button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              {currentStep < 4 ? (
                <Button onClick={handleNext} size="lg" type="button">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} size="lg" disabled={isSubmitting} type="button">
                  {isSubmitting ? "Submitting..." : "Complete Setup"}
                  <Check className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}
